import { __extends } from "tslib";
import { ColorTransform, Rectangle, Point, PerspectiveProjection, CoordinateSystem, Vector3D, Transform, ColorUtils } from '@awayjs/core';
import { Stage, BitmapImage2D, _Stage_BitmapImage2D, BlendMode, ContextGLBlendFactor, ContextGLTriangleFace, ImageUtils } from '@awayjs/stage';
import { RenderGroup, RendererType, Style } from '@awayjs/renderer';
import { DisplayObject } from '../display/DisplayObject';
import { DisplayObjectContainer } from '../display/DisplayObjectContainer';
import { SceneGraphPartition } from '../partition/SceneGraphPartition';
import { View } from '@awayjs/view';
import { MethodMaterial } from '@awayjs/materials';
import { Billboard } from '../display/Billboard';
import { Settings } from '../Settings';
// empty matrix for transfrorm reset
var TMP_COLOR_MATRIX = new ColorTransform();
var TMP_RAW = [];
var TMP_POINT = new Point(0, 0);
var TMP_RECT = new Rectangle();
/**
 *
 */
var SceneImage2D = /** @class */ (function (_super) {
    __extends(SceneImage2D, _super);
    /**
     * Creates a BitmapImage2D object with a specified width and height. If you
     * specify a value for the <code>fillColor</code> parameter, every pixel in
     * the bitmap is set to that color.
     *
     * <p>By default, the bitmap is created as transparent, unless you pass
     * the value <code>false</code> for the transparent parameter. After you
     * create an opaque bitmap, you cannot change it to a transparent bitmap.
     * Every pixel in an opaque bitmap uses only 24 bits of color channel
     * information. If you define the bitmap as transparent, every pixel uses 32
     * bits of color channel information, including an alpha transparency
     * channel.</p>
     *
     * @param width       The width of the bitmap image in pixels.
     * @param height      The height of the bitmap image in pixels.
     * @param transparent Specifies whether the bitmap image supports per-pixel
     *                    transparency. The default value is <code>true</code>
     *                    (transparent). To create a fully transparent bitmap,
     *                    set the value of the <code>transparent</code>
     *                    parameter to <code>true</code> and the value of the
     *                    <code>fillColor</code> parameter to 0x00000000(or to
     *                    0). Setting the <code>transparent</code> property to
     *                    <code>false</code> can result in minor improvements
     *                    in rendering performance.
     * @param fillColor   A 32-bit ARGB color value that you use to fill the
     *                    bitmap image area. The default value is
     *                    0xFFFFFFFF(solid white).
     */
    function SceneImage2D(width, height, transparent, fillColor, powerOfTwo, stage) {
        if (transparent === void 0) { transparent = true; }
        if (fillColor === void 0) { fillColor = 0xffffffff; }
        if (powerOfTwo === void 0) { powerOfTwo = true; }
        if (stage === void 0) { stage = null; }
        var _this = _super.call(this, width, height, transparent, null, powerOfTwo, stage) || this;
        // regions that already updated by getPixel, getPixels methods
        _this._updateRegions = [];
        _this._dirtyRegions = [];
        _this._maxDirtyArea = null;
        // when all SceneImageBitmap is updated
        _this._fullDirty = false;
        _this._initalFillColor = null;
        _this._lastUsedFill = null;
        _this._internalSync = false;
        /*private*/ _this._antialiasQuality = 0;
        _this._initalFillColor = fillColor;
        _this._lastUsedFill = fillColor;
        return _this;
    }
    SceneImage2D.getImage = function (width, height, transparent, fillColor, powerOfTwo, stage, msaa) {
        if (transparent === void 0) { transparent = true; }
        if (fillColor === void 0) { fillColor = 0xffffffff; }
        if (powerOfTwo === void 0) { powerOfTwo = true; }
        if (stage === void 0) { stage = null; }
        if (msaa === void 0) { msaa = false; }
        if (width > ImageUtils.MAX_SIZE || height > ImageUtils.MAX_SIZE) {
            throw "Try to create image greater that " + ImageUtils.MAX_SIZE + ", " + width + "x" + height;
        }
        var index = -1;
        for (var i = 0; i < this._pool.length; i++) {
            var e = this._pool[i];
            if ((!stage || e._stage === stage)
                && e.width === width
                && e.height === height
                && e.transparent === transparent
                && e.powerOfTwo === powerOfTwo
                && (msaa === (e._antialiasQuality > 0))) {
                index = i;
                break;
            }
        }
        if (index > -1) {
            var e = this._pool.splice(index, 1)[0];
            if (fillColor !== null)
                e.fillRect(e.rect, fillColor);
            return e;
        }
        else {
            if (this._pool.length === this.MAX_POOL_SIZE && this.MAX_POOL_SIZE < this.MAX_GROW_SIZE) {
                // GROW POOL WHEN THERE ARE NOT needed image size
                this.MAX_POOL_SIZE += this.MAX_POOL_SIZE * 0.25 | 0;
            }
        }
        var result = new SceneImage2D(width, height, transparent, fillColor, powerOfTwo, stage);
        if (msaa) {
            result._antialiasQuality = Settings.ALLOW_FORCE_MSAA;
        }
        return result;
    };
    SceneImage2D.tryStoreImage = function (image, force) {
        if (force === void 0) { force = false; }
        if (this._pool.length <= this.MAX_POOL_SIZE || force) {
            this._pool.push(image);
            return true;
        }
        return false;
    };
    SceneImage2D.getTemp = function (width, height, stage, msaa) {
        if (msaa === void 0) { msaa = false; }
        var max = Math.max(width, height);
        var size = this.TMP_STEP[0];
        for (var i = 0; i < this.TMP_STEP.length; i++) {
            if (max <= this.TMP_STEP[i]) {
                size = this.TMP_STEP[i];
                break;
            }
        }
        return this.getImage(size, size, true, null, true, stage, msaa);
    };
    Object.defineProperty(SceneImage2D.prototype, "antialiasQuality", {
        get: function () {
            return this._antialiasQuality;
        },
        enumerable: true,
        configurable: true
    });
    SceneImage2D.prototype.syncData = function (async) {
        var _this = this;
        if (async === void 0) { async = false; }
        if (async && this._asyncRead) {
            return this._asyncRead;
        }
        if (!async && this._asyncRead) {
            throw '[SceneImage2D] Synced read not allowed while async read is requested!';
        }
        this.applySymbol();
        // update data from pixels from GPU
        if (!this._imageDataDirty) {
            return async ? Promise.resolve(false) : false;
        }
        var context = this._stage.context;
        this._stage.setRenderTarget(this, false);
        // when we call syncData, we already loose other data
        // not require apply symbol etc, because it already should be applied
        if (!this._data) {
            this._data = new Uint8ClampedArray(this.width * this.height * 4);
        }
        // mark that this internal call, avoid reqursion loop
        this._internalSync = true;
        this._asyncRead = context.drawToBitmapImage2D(this, false, async);
        this._internalSync = false;
        this._stage.setRenderTarget(null);
        if (!async) {
            this.resetDirty();
            // we store pixel buffer already as PMA.
            // we should prevent unpack what already is PMA
            this._unpackPMA = false;
            return true;
        }
        return this._asyncRead.then(function (_status) {
            _this.resetDirty();
            _this._unpackPMA = false;
            _this._asyncRead = null;
            return true;
        });
    };
    /* overide internal */ SceneImage2D.prototype.getDataInternal = function (constructEmpty, skipSync) {
        if (constructEmpty === void 0) { constructEmpty = true; }
        if (skipSync === void 0) { skipSync = false; }
        // if sync called, check that this is requried
        if (!skipSync && this._imageDataDirty && !this._internalSync) {
            // sync data already should fill _data
            this.syncData(false);
            return this._data;
        }
        if (this._initalFillColor === null) {
            return _super.prototype.getDataInternal.call(this, constructEmpty, true);
        }
        // disable empty buffer filling
        // and check that buffer is empyt (has now symbol or alpha)
        var data = _super.prototype.getDataInternal.call(this, false, true);
        // if it empty, fill with initlal value
        if (!data) {
            // fill rect constuct buffer inside
            _super.prototype.fillRect.call(this, this.rect, this._initalFillColor);
            this._initalFillColor = null;
            return this._data;
        }
        return data;
    };
    /*
    public get data(): Uint8ClampedArray {
        this.syncData();

        return this._data || super.getDataInternal(true);
    }*/
    /**
     * Marks region as dirty for optiomisation for getPixel* methods
     */
    SceneImage2D.prototype.pushDirtyRegion = function (rect) {
        this._imageDataDirty = true;
        if (this._fullDirty) {
            if (this._updateRegions && this._updateRegions.length) {
                this._updateRegions.length = 0;
            }
            return;
        }
        var eq = rect.equals(this._rect);
        if (!this._dirtyRegions) {
            this._dirtyRegions = [];
        }
        if (!eq) {
            this._dirtyRegions.push(rect);
        }
        else {
            this._dirtyRegions = [this._rect];
            this._fullDirty = true;
        }
        if (!this._maxDirtyArea || eq) {
            this._maxDirtyArea = rect.clone();
        }
        else {
            var rr = rect._rawData;
            var ma = this._maxDirtyArea;
            var mr = ma._rawData;
            mr[0] = rr[0] < mr[0] ? rr[0] : mr[0];
            mr[1] = rr[1] < mr[1] ? rr[1] : mr[1];
            ma.right = ma.right < rect.right ? ma.right : rect.right;
            ma.bottom = ma.bottom < rect.bottom ? ma.bottom : rect.bottom;
            //clamp
            if (mr[0] < 0)
                (mr[0] = 0);
            if (mr[1] < 0)
                (mr[1] = 0);
            if (mr[2] > this.width)
                (mr[2] = this.width);
            if (mr[3] > this.height)
                (mr[3] = this.height);
        }
        if (this._updateRegions) {
            for (var i = this._updateRegions.length - 1; i >= 0; i--) {
                if (this._updateRegions[i].intersects(rect)) {
                    this._updateRegions.splice(i, 1);
                }
            }
        }
    };
    /**
     *
     */
    SceneImage2D.prototype.resetDirty = function () {
        this._dirtyRegions = [];
        this._updateRegions = [];
        this._maxDirtyArea = null;
        this._fullDirty = false;
        this._imageDataDirty = false;
    };
    Object.defineProperty(SceneImage2D.prototype, "assetType", {
        /**
         *
         * @returns {string}
         */
        get: function () {
            return SceneImage2D.assetType;
        },
        enumerable: true,
        configurable: true
    });
    SceneImage2D.prototype.createRenderer = function () {
        //create the projection
        var projection = new PerspectiveProjection();
        projection.coordinateSystem = CoordinateSystem.RIGHT_HANDED;
        projection.originX = -1;
        projection.originY = 1;
        //create the view
        SceneImage2D._root = new DisplayObjectContainer();
        SceneImage2D._renderer = RenderGroup.getInstance(new View(projection, this._stage, null, null, null, true), RendererType.DEFAULT)
            .getRenderer(new SceneGraphPartition(SceneImage2D._root));
        SceneImage2D._root.partition = SceneImage2D._renderer.partition;
        //SceneImage2D._renderer.antiAlias = Settings.ALLOW_FORCE_MSAA;
        //setup the projection
        SceneImage2D._renderer.disableClear = true;
        SceneImage2D._renderer.view.backgroundAlpha = 0;
        SceneImage2D._renderer.view.projection = projection;
        SceneImage2D._renderer.view.projection.transform = new Transform();
        SceneImage2D._renderer.view.projection.transform.moveTo(0, 0, -1000);
        SceneImage2D._renderer.view.projection.transform.lookAt(new Vector3D());
        SceneImage2D._renderer.renderableSorter = null; //new RenderableSort2D();
    };
    SceneImage2D.prototype.createBillboardRenderer = function () {
        //create the projection
        var projection = new PerspectiveProjection();
        projection.coordinateSystem = CoordinateSystem.RIGHT_HANDED;
        projection.originX = -1;
        projection.originY = 1;
        //create the view
        SceneImage2D._billboardRoot = new DisplayObjectContainer();
        SceneImage2D._billboardRenderer = RenderGroup.getInstance(new View(projection, this._stage, null, null, null, true), RendererType.DEFAULT)
            .getRenderer(new SceneGraphPartition(SceneImage2D._billboardRoot));
        SceneImage2D._billboardRoot.partition = SceneImage2D._billboardRenderer.partition;
        //SceneImage2D._renderer.antiAlias = Settings.ALLOW_FORCE_MSAA;
        //setup the projection
        SceneImage2D._billboardRenderer.disableClear = true;
        SceneImage2D._billboardRenderer.view.backgroundAlpha = 0;
        SceneImage2D._billboardRenderer.view.projection = projection;
        SceneImage2D._billboardRenderer.view.projection.transform = new Transform();
        SceneImage2D._billboardRenderer.view.projection.transform.moveTo(0, 0, -1000);
        SceneImage2D._billboardRenderer.view.projection.transform.lookAt(new Vector3D());
        SceneImage2D._billboardRenderer.renderableSorter = null; //new RenderableSort2D();
        var mat = new MethodMaterial(new BitmapImage2D(128, 128, true, 0x0));
        mat.bothSides = true;
        mat.alphaBlending = true;
        SceneImage2D._billboard = new Billboard(mat);
        SceneImage2D._billboard.style = new Style();
        SceneImage2D._billboardRoot.addChild(SceneImage2D._billboard);
    };
    /**
     * Frees memory that is used to store the BitmapImage2D object.
     *
     * <p>When the <code>dispose()</code> method is called on an image, the width
     * and height of the image are set to 0. All subsequent calls to methods or
     * properties of this BitmapImage2D instance fail, and an exception is thrown.
     * </p>
     *
     * <p><code>BitmapImage2D.dispose()</code> releases the memory occupied by the
     * actual bitmap data, immediately(a bitmap can consume up to 64 MB of
     * memory). After using <code>BitmapImage2D.dispose()</code>, the BitmapImage2D
     * object is no longer usable and an exception may be thrown if
     * you call functions on the BitmapImage2D object. However,
     * <code>BitmapImage2D.dispose()</code> does not garbage collect the BitmapImage2D
     * object(approximately 128 bytes); the memory occupied by the actual
     * BitmapImage2D object is released at the time the BitmapImage2D object is
     * collected by the garbage collector.</p>
     *
     */
    SceneImage2D.prototype.dispose = function () {
        this.dropAllReferences();
        this.unmarkToUnload();
        this.unuseWeakRef();
        this._dirtyRegions = null;
        this._updateRegions = null;
        this._maxDirtyArea = null;
        // drop buffer, because is big
        this._data = null;
        this._locked = false;
        if (!this.wasUpload || !SceneImage2D.tryStoreImage(this, false)) {
            _super.prototype.dispose.call(this);
        }
        //todo
    };
    SceneImage2D.prototype.unload = function () {
        var _this = this;
        // query asynce unload
        var t = this.syncData(true);
        // strict quard
        if (typeof t !== 'boolean') {
            t.then(function () { return _super.prototype.unload.call(_this); });
            return;
        }
        _super.prototype.unload.call(this);
    };
    SceneImage2D.prototype.deepClone = function (from) {
        this.copyPixels(from, this._rect, new Point(0, 0));
    };
    SceneImage2D.prototype.clone = function () {
        var clone = SceneImage2D.getImage(this.width, this.height, this.transparent, null, false, this._stage);
        this.addNestedReference(clone);
        return clone;
    };
    /**
     * Fills a rectangular area of pixels with a specified ARGB color.
     *
     * @param rect  The rectangular area to fill.
     * @param color The ARGB color value that fills the area. ARGB colors are
     *              often specified in hexadecimal format; for example,
     *              0xFF336699.
     * @throws TypeError The rect is null.
     */
    SceneImage2D.prototype.fillRect = function (rect, color) {
        this.dropAllReferences();
        var argb = ColorUtils.float32ColorToARGB(color);
        var alpha = this._transparent ? argb[0] / 255 : 1;
        var isCrop = rect !== this._rect && !this._rect.equals(rect);
        this._stage.setRenderTarget(this, true);
        if (isCrop) {
            this._stage.setScissor(rect);
        }
        // we shure that color is fully filled when there are not any crops
        this._lastUsedFill = isCrop ? null : color;
        this._stage.clear((argb[1] / 0xff) * alpha | 0, (argb[2] / 0xff) * alpha | 0, (argb[3] / 0xff) * alpha | 0, alpha);
        if (isCrop) {
            this._stage.setScissor(null);
        }
        this.pushDirtyRegion(rect);
    };
    /**
     * Provides a fast routine to perform pixel manipulation between images with
     * no stretching, rotation, or color effects. This method copies a
     * rectangular area of a source image to a rectangular area of the same size
     * at the destination point of the destination BitmapImage2D object.
     *
     * <p>If you include the <code>alphaBitmap</code> and <code>alphaPoint</code>
     * parameters, you can use a secondary image as an alpha source for the
     * source image. If the source image has alpha data, both sets of alpha data
     * are used to composite pixels from the source image to the destination
     * image. The <code>alphaPoint</code> parameter is the point in the alpha
     * image that corresponds to the upper-left corner of the source rectangle.
     * Any pixels outside the intersection of the source image and alpha image
     * are not copied to the destination image.</p>
     *
     * <p>The <code>mergeAlpha</code> property controls whether or not the alpha
     * channel is used when a transparent image is copied onto another
     * transparent image. To copy pixels with the alpha channel data, set the
     * <code>mergeAlpha</code> property to <code>true</code>. By default, the
     * <code>mergeAlpha</code> property is <code>false</code>.</p>
     *
     * @param sourceBitmapImage2D The input bitmap image from which to copy pixels.
     *                         The source image can be a different BitmapImage2D
     *                         instance, or it can refer to the current
     *                         BitmapImage2D instance.
     * @param sourceRect       A rectangle that defines the area of the source
     *                         image to use as input.
     * @param destPoint        The destination point that represents the
     *                         upper-left corner of the rectangular area where
     *                         the new pixels are placed.
     * @param alphaBitmapImage2D  A secondary, alpha BitmapImage2D object source.
     * @param alphaPoint       The point in the alpha BitmapImage2D object source
     *                         that corresponds to the upper-left corner of the
     *                         <code>sourceRect</code> parameter.
     * @param mergeAlpha       To use the alpha channel, set the value to
     *                         <code>true</code>. To copy pixels with no alpha
     *                         channel, set the value to <code>false</code>.
     * @throws TypeError The sourceBitmapImage2D, sourceRect, destPoint are null.
     */
    SceneImage2D.prototype.copyPixels = function (source, sourceRect, destPoint, alphaBitmapData, alphaPoint, mergeAlpha) {
        this._lastUsedFill = null;
        this.dropAllReferences();
        this.unmarkToUnload();
        this._stage.context.setCulling(ContextGLTriangleFace.NONE);
        this._stage.context.setBlendFactors(ContextGLBlendFactor.ONE, ContextGLBlendFactor.ONE_MINUS_SOURCE_ALPHA);
        // need drop alpha from source when target is not has alpha
        mergeAlpha = this.transparent !== source.transparent || mergeAlpha;
        if (this._initalFillColor !== null) {
            this.fillRect(this._rect, this._initalFillColor);
            this._initalFillColor = null;
        }
        if (source !== this) {
            this._stage.copyPixels(source, this, sourceRect, destPoint, alphaBitmapData, alphaPoint, mergeAlpha);
        }
        else {
            var tmp = SceneImage2D.getTemp(source.width, source.height, this._stage);
            if (tmp._antialiasQuality > 0) {
                throw 'Using MSAA texture for copy pixel!';
            }
            TMP_POINT.setTo(0, 0);
            TMP_RECT.setTo(0, 0, sourceRect.width, sourceRect.height);
            this._stage.copyPixels(source, tmp, sourceRect, TMP_POINT, alphaBitmapData, alphaPoint, false);
            this._stage.copyPixels(tmp, this, TMP_RECT, destPoint, alphaBitmapData, alphaPoint, mergeAlpha);
            // push temp back
            SceneImage2D.tryStoreImage(tmp, true);
        }
        this.pushDirtyRegion(new Rectangle(destPoint.x, destPoint.y, sourceRect.width, sourceRect.height));
    };
    SceneImage2D.prototype.threshold = function (source, sourceRect, destPoint, operation, threshold, color, mask, copySource) {
        this._lastUsedFill = null;
        this.dropAllReferences();
        this.unmarkToUnload();
        this._stage.context.setCulling(ContextGLTriangleFace.NONE);
        this._stage.context.setBlendFactors(ContextGLBlendFactor.ONE, ContextGLBlendFactor.ZERO);
        var renderToSelf = source === this;
        if (!renderToSelf) {
            this._stage.threshold(source, this, sourceRect, destPoint, operation, threshold, color, mask, copySource);
        }
        else {
            var tmp = SceneImage2D.getTemp(source.width, source.height, this._stage);
            TMP_POINT.setTo(0, 0);
            TMP_RECT.setTo(0, 0, sourceRect.width, sourceRect.height);
            this._stage.threshold(source, tmp, sourceRect, TMP_POINT, operation, threshold, color, mask, copySource);
            this._stage.copyPixels(tmp, this, TMP_RECT, destPoint, null, null, false);
            // push temp back
            SceneImage2D.tryStoreImage(tmp, true);
        }
        this.pushDirtyRegion(new Rectangle(destPoint.x, destPoint.y, sourceRect.width, sourceRect.height));
        this._imageDataDirty = true;
    };
    SceneImage2D.prototype.colorTransform = function (rect, colorTransform) {
        this._lastUsedFill = null;
        this.dropAllReferences();
        this.unmarkToUnload();
        this._stage.context.setCulling(ContextGLTriangleFace.NONE);
        this._stage.context.setBlendFactors(ContextGLBlendFactor.ONE, ContextGLBlendFactor.ZERO);
        var tmp = SceneImage2D.getTemp(rect.x + rect.width, rect.y + rect.height, this._stage);
        this._stage.colorTransform(this, tmp, rect, colorTransform);
        this._stage.copyPixels(tmp, this, rect, new Point(0, 0), null, null, false);
        this.pushDirtyRegion(new Rectangle(rect.x, rect.y, rect.width, rect.height));
        this._imageDataDirty = true;
        SceneImage2D.tryStoreImage(tmp, true);
    };
    /**
     * @inheritdoc
     */
    SceneImage2D.prototype.getPixel32 = function (x, y) {
        this.syncData();
        return _super.prototype.getPixel32.call(this, x, y);
    };
    /**
     * @inheritdoc
     */
    SceneImage2D.prototype.getPixel = function (x, y) {
        var result = this.getPixel32(x, y);
        return result & 0x00ffffff;
    };
    SceneImage2D.prototype.draw = function (source, matrix, colorTransform, blendMode, clipRect, smoothing) {
        /* eslint-enable */
        this.dropAllReferences();
        this.unmarkToUnload();
        if (source instanceof DisplayObject) {
            this._drawAsDisplay(source, matrix, colorTransform, blendMode, clipRect, smoothing);
        }
        else {
            this._drawAsBitmap(source, matrix, colorTransform, blendMode, clipRect, smoothing);
        }
        this._lastUsedFill = null;
        //TODO implement passing real updated region
        this.pushDirtyRegion(this._rect);
        this._imageDataDirty = true;
    };
    SceneImage2D.prototype._mapBlendMode = function (blendMode) {
        if (blendMode === void 0) { blendMode = ''; }
        switch (blendMode) {
            case null:
            case '':
            case BlendMode.NORMAL:
            case BlendMode.LAYER:
                return BlendMode.LAYER;
            case BlendMode.MULTIPLY:
            case BlendMode.ADD:
            case BlendMode.ALPHA:
                return blendMode;
        }
        //console.debug("[ImageBitmap] Unsupport BlendMode", blendMode);
        return BlendMode.LAYER;
    };
    SceneImage2D.prototype._drawAsBitmap = function (source, matrix, colorTransform, blendMode, clipRect, smoothing) {
        if (!SceneImage2D._billboardRenderer)
            this.createBillboardRenderer();
        if (this._initalFillColor !== null) {
            this.fillRect(this._rect, this._initalFillColor);
            this._initalFillColor = null;
        }
        var renderer = SceneImage2D._billboardRenderer;
        var root = SceneImage2D._billboardRoot;
        var billboard = SceneImage2D._billboard;
        billboard.sampler.smooth = smoothing;
        renderer.disableClear = true;
        renderer.view.target = this;
        renderer.view.projection.scale = 1000 / this.rect.height;
        billboard.material.style.image = source;
        billboard.material.blendMode = this._mapBlendMode(blendMode);
        billboard.material.useColorTransform = !!colorTransform;
        if (matrix) {
            var m = root.transform.matrix3D;
            m.identity();
            m._rawData[0] = matrix.a;
            m._rawData[1] = -matrix.b;
            m._rawData[4] = matrix.c;
            m._rawData[5] = -matrix.d;
            m._rawData[12] = matrix.tx;
            m._rawData[13] = this.rect.height - matrix.ty;
            root.transform.invalidateComponents();
        }
        else {
            root.transform.rotateTo(0, 0, 0);
            root.transform.scaleTo(1, -1, 1);
            root.transform.moveTo(0, this.rect.height, 0);
        }
        root.transform.colorTransform = colorTransform;
        //render
        renderer.render();
    };
    SceneImage2D.prototype.fastScaledNativeCopy = function (from, to, sourceRect) {
        if (sourceRect === void 0) { sourceRect = null; }
        var context = this._stage.context;
        var tex = context._texContext;
        this._stage.setScissor(null);
        this._stage.setRenderTarget(from, false);
        var target = to.getAbstraction(this._stage).getTexture();
        tex.unsafeCopyToTexture(target, sourceRect || to._rect, TMP_POINT, true);
    };
    SceneImage2D.prototype._drawAsDisplay = function (source, matrix, colorTransform, blendMode, clipRect, smoothing) {
        if (!SceneImage2D._renderer)
            this.createRenderer();
        // if need use MSAA, we create temporary image and draw to it
        var nativeMSAA = this._stage.context.glVersion === 2 && Settings.ALLOW_FORCE_MSAA > 1;
        var emulatedMSAA = !nativeMSAA && Settings.ALLOW_APPROXIMATION > 1;
        var target = this;
        var scaleRatio = 1;
        // lazy filling
        if (!nativeMSAA && !emulatedMSAA && this._initalFillColor !== null) {
            this.fillRect(this._rect, this._initalFillColor);
            this._initalFillColor = null;
        }
        if (nativeMSAA) {
            target = SceneImage2D.getTemp(this.width, this.height, this._stage, true);
            if (this._lastUsedFill !== null) {
                target.fillRect(target._rect, this._lastUsedFill);
            }
            else {
                // copy from source to tmp
                TMP_POINT.setTo(0, 0);
                // we cant use mergeAlpha = false, becasue RT is MSAA
                // MSAA texture is immutable
                //target.fillRect(target._rect, 0x0);
                this._stage.copyPixels(this, target, this._rect, TMP_POINT, null, null, true);
            }
        }
        else if (emulatedMSAA) {
            scaleRatio = Settings.ALLOW_APPROXIMATION;
            target = SceneImage2D.getTemp(this.width * scaleRatio, this.height * scaleRatio, this._stage, false);
            if (this._lastUsedFill !== null) {
                target.fillRect(target._rect, this._lastUsedFill);
            }
            else {
                // copy from source to tmp
                // BUT FOR THIS REQUIRED THAT SAMPLER IS LINEAR
                this.fastScaledNativeCopy(this, target, this._rect);
            }
        }
        var root = SceneImage2D._root;
        var renderer = SceneImage2D._renderer;
        var oldVisible = source.visible;
        var oldParent = source.parent;
        if (oldParent)
            oldParent.adapter.getChildForDraw(source);
        var sTrans = source.transform;
        // clone TRS separatenly, because matrix saving/restoring is bugged, ex
        // this works for all knowed cases
        for (var i = 0; i < 4; i++) {
            TMP_RAW[0 + i] = sTrans.position._rawData[i];
            TMP_RAW[4 + i] = sTrans.rotation._rawData[i];
            TMP_RAW[8 + i] = sTrans.scale._rawData[i];
        }
        // same as matrix
        TMP_COLOR_MATRIX.copyRawDataFrom(source.transform.colorTransform._rawData);
        // need correcting a root because maybe flipped around Z
        var zFlip = source.transform.matrix3D._rawData[10];
        if (matrix) {
            var m = root.transform.matrix3D;
            m.identity();
            m._rawData[0] = matrix.a;
            m._rawData[1] = -matrix.b;
            m._rawData[4] = matrix.c;
            m._rawData[5] = -matrix.d;
            m._rawData[10] = zFlip;
            m._rawData[12] = matrix.tx;
            m._rawData[13] = this._rect.height - matrix.ty;
            root.transform.invalidateComponents();
        }
        else {
            root.transform.rotateTo(0, 0, 0);
            root.transform.scaleTo(1, -1, 1);
            root.transform.moveTo(0, this._rect.height, 0);
        }
        renderer.antiAlias = target._antialiasQuality || 0;
        renderer.view.target = target;
        renderer.view.projection.scale = 1000 / this._rect.height;
        if (emulatedMSAA) {
            var thisRatio = (this._rect.width / this._rect.height);
            var targetRatio = 1; // TMP should be quad;
            // because we render a rect with ratio !=1 in rect with ratio === 1, apply needed
            renderer.view.projection.ratio = thisRatio / targetRatio;
            renderer.view.x = 0;
            renderer.view.y = 0;
            renderer.view.width = target.width;
            renderer.view.height = target.height;
        }
        else {
            // shift view, because target can be more
            renderer.view.projection.ratio = (this._rect.width / this._rect.height);
            renderer.view.x = -(target.width - this.width);
            renderer.view.y = -(target.height - this.height);
            renderer.view.width = this.width;
            renderer.view.height = this.height;
        }
        root.removeChildren(0, root.numChildren);
        root.addChild(source);
        root.transform.colorTransform = colorTransform;
        source.transform.matrix3D = null;
        source.visible = true;
        source.transform.colorTransform = null;
        blendMode = blendMode || source.blendMode || '';
        root.blendMode = this._mapBlendMode(blendMode);
        //save snapshot if unlocked
        //if (!this._locked)
        //SceneImage2D.scene.view.target=this;
        //SceneImage2D.scene.renderer.disableClear = !this._locked;
        //render
        renderer.render();
        renderer.antiAlias = 0;
        source.visible = oldVisible;
        //source.transform.matrix3D = TMP_MATRIX3D;
        sTrans.moveTo(TMP_RAW[0 + 0], TMP_RAW[0 + 1], TMP_RAW[0 + 2]);
        sTrans.rotateTo(TMP_RAW[4 + 0], TMP_RAW[4 + 1], TMP_RAW[4 + 2]);
        sTrans.scaleTo(TMP_RAW[8 + 0], TMP_RAW[8 + 1], TMP_RAW[8 + 2]);
        source.transform.colorTransform = TMP_COLOR_MATRIX;
        root.removeChild(source);
        if (oldParent) {
            oldParent.adapter.returnChildAfterDraw(source);
        }
        if (nativeMSAA) {
            // becasue we copy MSAA into no msaa, it should passed as BLIT
            this._stage.copyPixels(target, this, this._rect, TMP_POINT, null, null, false);
            // clean before push
            // should works without this, but i don't know why bugged
            target.fillRect(target._rect, 0x0);
            SceneImage2D.tryStoreImage(target, true);
        }
        else if (emulatedMSAA) {
            // copy with downsale
            this.fastScaledNativeCopy(target, this, target._rect);
            // clean before push
            // should works without this, but i don't know why bugged
            target.fillRect(target._rect, 0x0);
            SceneImage2D.tryStoreImage(target, true);
        }
        //SceneImage2D.scene.dispose();
        //SceneImage2D.scene=null;
    };
    SceneImage2D.assetType = '[image SceneImage2D]';
    SceneImage2D.MAX_POOL_SIZE = 30;
    SceneImage2D.MAX_GROW_SIZE = 300; // x10 of start value
    SceneImage2D._pool = [];
    SceneImage2D.TMP_STEP = [128, 256, 512, 1024, 2048, 4096];
    return SceneImage2D;
}(BitmapImage2D));
export { SceneImage2D };
Stage.registerAbstraction(_Stage_BitmapImage2D, SceneImage2D);
